import { NumberInput } from "../commons/NumberInput";
import * as Maths from "oc-commons/Maths";
import { OptionSelect, TitleBlock } from "./OptionSelect";
import sprintf from "sprintf-js";
import { I18N } from "oc-I18N";
import * as _ from "lodash";
import { MAuswahl } from "../../metamodell";
import { getAuswahlData } from "./MHelper";
import { findAuswahl } from "../arrangement/elementFunctions";
import { SizeInput } from "../components/SizeInput";
import { Konfiguration } from "./Konfiguration";

const eantrieb_ueberstand_image = require("../style/bilder/eantrieb_ueberstand.png");

/**
 * Saubere Definition der Masse-Keys
 */
export enum MASSE {
  Breite = "Breite",
  BreiteGehflügel = "Breite Gehflügel",
  BreiteStandflügel = "Breite Standflügel",
  BreiteEinzeltor = "Breite Einzeltor",
  LichteWeite = "Lichte Weite",
  LichteWeiteEinzeltor = "Lichte Weite Einzeltor",
  Gesamtbreite = "Gesamtbreite",
  Platzbedarf = "Platzbedarf",
  Höhe = "Höhe",
  PfostenHöhe = "Pfostenhöhe",
  Anschlagprofil = "Anschlagprofilbreite",
  PlatzbedarfSchiebetor = "Platzbedarf Schiebetor",
  PlatzbedarfSchiebetorInklusiveFundament = "Platzbedarf Schiebetor inklusive Fundament",
  PlatzbedarfSchiebetorTiefe = "Platzbedarf Schiebetor Tiefe",
}

/**
 * Mass-Eingaben-Seite für den Torkonfigurator
 * @param title
 * @constructor
 */
export function Masse({ title }: { title: string }) {
  const konf = APP.arrangement.konfiguration;
  const isDoppeltor = konf.isDoppeltor();
  const isDreifachtor = konf.isDreifachtor();
  const isSchiebetor = konf.isSchiebetor();
  const isTorWorkflow = konf.isTorWorkflow();
  const isAnMauer = konf.isAnMauer();

  const anzahlElementeLichteWeite = isDoppeltor || isDreifachtor ? 2 : 1;
  const lichteWeiteZusatz = konf.getLichteWeiteZusatz();
  const lichteWeiteZusatzEinzeltor = konf.getLichteWeiteZusatz(konf.currentElementEinzeltor);
  const anschlagprofilZusatz = konf.getAnschlagprofilZusatz();
  const anzahlElementeGesamtBreite = isDreifachtor ? 3 : isDoppeltor ? 2 : 1;
  const gesamtBreiteZusatz = konf.getGesamtBreiteZusatz();

  const titleKey = "Masse";
  const auswahlDataPage = getAuswahlData(titleKey);

  const auswahlDatasMasse = getAndFillAuswahlDataMasse();

  const showMassAuswahl = konf.isShowMassAuswahl();

  const element = konf.currentElement;
  const minMaxBox = element.sizeRange.flügelBox.clone();
  const eps = 0.00001;
  minMaxBox.min.addScalar(-eps);
  minMaxBox.max.addScalar(eps);

  const einzeltor_element = konf.currentElementEinzeltor;
  const einzeltor_minMaxBox = einzeltor_element?.sizeRange.flügelBox;
  const breiteDisabled =
    !element.isBreiteKonfigurierbar || !(minMaxBox.min.x + 0.001 < minMaxBox.max.x);
  const höheDisabled =
    !element.isBreiteKonfigurierbar || !(minMaxBox.min.y + 0.001 < minMaxBox.max.y);

  return (
    <section id="content_mass" aria-labelledby="masse-title">
      {showMassAuswahl && (
        <OptionSelect key={"Mass"} optionKey={"Mass"} optionTitleKey={"Mass"}></OptionSelect>
      )}

      <div className="optionSelect Masse">
        <TitleBlock auswahlData={auswahlDataPage} />
        <div className="options" role="group" aria-label={I18N("aria::Maßeingaben")}>
          {/** Zeile1: Breite bzw. Gehflügel + Standflügel + Luft = Lichte Weite **/}
          <fieldset className="optionRow">
            <legend className="sr-only">{I18N("aria::Breiteneinstellungen")}</legend>
            <div className="inputs">
              {!isDoppeltor && !isDreifachtor && !isSchiebetor && (
                <Eingabe
                  what={MASSE.Breite}
                  auswahlData={auswahlDatasMasse[MASSE.Breite]}
                  min={minMaxBox.min.x}
                  max={minMaxBox.max.x}
                  disabled={breiteDisabled || konf.isAnMauerBeide()}
                  textColor={APP.settings.massFarben.Breite.getHexString()}
                />
              )}
              {(isDoppeltor || isDreifachtor) && (
                <>
                  <Eingabe
                    what={MASSE.BreiteGehflügel}
                    auswahlData={auswahlDatasMasse[MASSE.BreiteGehflügel]}
                    min={minMaxBox.min.x}
                    max={minMaxBox.max.x}
                    disabled={breiteDisabled}
                    textColor={APP.settings.massFarben.Gehflügel.getHexString()}
                  />
                  <ZwischenText text={"&nbsp;+&nbsp;"} />
                  <Eingabe
                    what={MASSE.BreiteStandflügel}
                    auswahlData={auswahlDatasMasse[MASSE.BreiteStandflügel]}
                    min={minMaxBox.min.x}
                    max={minMaxBox.max.x}
                    disabled={breiteDisabled}
                    textColor={APP.settings.massFarben.Standflügel.getHexString()}
                  />
                </>
              )}
              {!isSchiebetor && (
                <LichteWeiteZusatz
                  lichteWeiteZusatz={lichteWeiteZusatz}
                  equalSign={anschlagprofilZusatz <= 0}
                />
              )}
              {anschlagprofilZusatz > 0 && (
                <LichteWeiteZusatz
                  lichteWeiteZusatz={anschlagprofilZusatz}
                  title={auswahlDatasMasse[MASSE.Anschlagprofil].Text}
                />
              )}
              <Eingabe
                what={MASSE.LichteWeite}
                auswahlData={auswahlDatasMasse[MASSE.LichteWeite]}
                min={minMaxBox.min.x * anzahlElementeLichteWeite + lichteWeiteZusatz}
                max={minMaxBox.max.x * anzahlElementeLichteWeite + lichteWeiteZusatz}
                disabled={breiteDisabled}
                textColor={APP.settings.massFarben.LichteWeite.getHexString()}
              />
            </div>
          </fieldset>

          {/** Zeile1b: Breite Dreifachtor + Luft = Lichte Weite **/}
          {isDreifachtor && (
            <fieldset className="optionRow">
              <legend className="sr-only">{I18N("aria::Einzeltor Breiteneinstellungen")}</legend>
              <div className="inputs">
                <Eingabe
                  what={MASSE.BreiteEinzeltor}
                  auswahlData={auswahlDatasMasse[MASSE.BreiteEinzeltor]}
                  min={einzeltor_minMaxBox.min.x}
                  max={einzeltor_minMaxBox.max.x}
                  disabled={breiteDisabled}
                  textColor={APP.settings.massFarben.Einzeltor.getHexString()}
                />
                <LichteWeiteZusatz lichteWeiteZusatz={lichteWeiteZusatzEinzeltor} />
                <Eingabe
                  what={MASSE.LichteWeiteEinzeltor}
                  auswahlData={auswahlDatasMasse[MASSE.LichteWeiteEinzeltor]}
                  min={einzeltor_minMaxBox.min.x + lichteWeiteZusatzEinzeltor}
                  max={einzeltor_minMaxBox.max.x + lichteWeiteZusatzEinzeltor}
                  disabled={breiteDisabled}
                  textColor={APP.settings.massFarben.LichteWeite.getHexString()}
                />
              </div>
            </fieldset>
          )}

          {/** Zeile2: Lichte Weite + Pfosten = Gesamtbreite **/}
          {!isSchiebetor && isTorWorkflow && !isAnMauer && (
            <fieldset className="optionRow">
              <legend className="sr-only">{I18N("aria::Gesamtbreite")}</legend>
              <div className="inputs">
                <>
                  <ZwischenText text={auswahlDatasMasse[MASSE.LichteWeite].Text} />
                  {isDreifachtor && (
                    <ZwischenText
                      text={`&nbsp;+&nbsp;${auswahlDatasMasse[MASSE.LichteWeiteEinzeltor].Text}`}
                    />
                  )}
                  <ZwischenText text={`&nbsp;+&nbsp;${I18N("Torpfosten")}&nbsp;=&nbsp;`} />
                  <Eingabe
                    what={MASSE.Gesamtbreite}
                    auswahlData={auswahlDatasMasse[MASSE.Gesamtbreite]}
                    min={minMaxBox.min.x * anzahlElementeGesamtBreite + gesamtBreiteZusatz}
                    max={minMaxBox.max.x * anzahlElementeGesamtBreite + gesamtBreiteZusatz}
                    disabled={breiteDisabled}
                    textColor={APP.settings.massFarben.Gesamtbreite.getHexString()}
                  />
                </>
                {konf.isDoppeltorEAntrieb() && <EAntriebPlatzbedarf konfiguration={konf} />}
              </div>
            </fieldset>
          )}

          {/** Zeile2a: Platzbedarf zum öffnen des Tores **/}
          {isSchiebetor && (
            <fieldset className="optionRow">
              <legend className="sr-only">{I18N("aria::PlatzbedarfSchiebetor")}</legend>
              <div className="inputs">
                <>
                  <Eingabe
                    what={MASSE.PlatzbedarfSchiebetor}
                    auswahlData={auswahlDatasMasse[MASSE.PlatzbedarfSchiebetor]}
                    min={-Infinity}
                    max={Infinity}
                    disabled={true}
                    textColor={APP.settings.massFarben.PlatzbedarfSchiebetor.getHexString()}
                  />
                </>
              </div>
            </fieldset>
          )}

          {/** Zeile2b: Platzbedarf zum öffnen des Tores inklusive Fundamentüberstand x Platzbedarf für Fundamente in der Tiefe **/}
          {isSchiebetor && (
            <fieldset className="optionRow">
              <legend className="sr-only">
                {I18N("aria::PlatzbedarfSchiebetorInklusiveFundamentxPlatzbedarfTiefe")}
              </legend>
              <div className="inputs">
                <>
                  <Eingabe
                    what={MASSE.PlatzbedarfSchiebetorInklusiveFundament}
                    auswahlData={auswahlDatasMasse[MASSE.PlatzbedarfSchiebetorInklusiveFundament]}
                    min={-Infinity}
                    max={Infinity}
                    disabled={true}
                    textColor={APP.settings.massFarben.PlatzbedarfSchiebetorInklusiveFundament.getHexString()}
                  />
                  <ZwischenText text={`&nbsp; x &nbsp;`} />
                  <Eingabe
                    what={MASSE.PlatzbedarfSchiebetorTiefe}
                    auswahlData={auswahlDatasMasse[MASSE.PlatzbedarfSchiebetorTiefe]}
                    min={-Infinity}
                    max={Infinity}
                    disabled={true}
                    textColor={APP.settings.massFarben.PlatzbedarfSchiebetorTiefe.getHexString()}
                  />
                </>
              </div>
            </fieldset>
          )}

          {/** Zeile3: Höhe **/}
          <fieldset className="optionRow">
            <legend className="sr-only">{I18N("aria::Höheneinstellungen")}</legend>
            <div className="inputs">
              <Eingabe
                what={MASSE.Höhe}
                auswahlData={auswahlDatasMasse[MASSE.Höhe]}
                min={minMaxBox.min.y}
                max={minMaxBox.max.y}
                disabled={höheDisabled}
                axis={"y"}
                textColor={APP.settings.massFarben.Höhe.getHexString()}
              />
            </div>
          </fieldset>
        </div>

        {APP.arrangement.getCurrentElement().isEAntrieb && !isSchiebetor && (
          <aside
            className={"eantrieb_ueberstand"}
            aria-label={I18N("aria::E-Antrieb Überstand Hinweis")}
          >
            <img
              src={eantrieb_ueberstand_image}
              alt={I18N("aria::E-Antrieb Überstand Illustration")}
            ></img>
            <div
              dangerouslySetInnerHTML={{
                __html: findAuswahl("E_Antrieb_Vorhanden")?.Hilfstext ?? "",
              }}
              role="note"
            ></div>
          </aside>
        )}
      </div>
    </section>
  );
}

/**
 * Gibt die Auswahldaten für Masse zurück. Füllt gleichzeitig den Hilfstext mit min- und max-Werten
 */
function getAndFillAuswahlDataMasse() {
  const konf = APP.arrangement.konfiguration;
  const minMaxBox = konf.currentElement.sizeRange.flügelBox;
  const minMaxBoxEinzeltor = konf.currentElementEinzeltor?.sizeRange.flügelBox;
  const lichteWeiteZusatz = konf.getLichteWeiteZusatz();
  const lichteWeiteZusatzEinzeltor = konf.getLichteWeiteZusatz(konf.currentElementEinzeltor);
  const anschlagprofilZusatz = konf.getAnschlagprofilZusatz();
  const gesamtBreiteZusatz = konf.getGesamtBreiteZusatz();
  const minX = minMaxBox.min.x;
  const maxX = minMaxBox.max.x;
  const minY = minMaxBox.min.y;
  const maxY = minMaxBox.max.y;
  const nameIntervallMapping = {
    [MASSE.Breite]: [minX, maxX],
    [MASSE.BreiteGehflügel]: [minX, maxX],
    [MASSE.BreiteStandflügel]: [minX, maxX],
    [MASSE.BreiteEinzeltor]: [minMaxBoxEinzeltor?.min.x, minMaxBoxEinzeltor?.max.x],
    [MASSE.LichteWeite]: [minX + lichteWeiteZusatz, maxX + lichteWeiteZusatz],
    [MASSE.LichteWeiteEinzeltor]: [
      minX + lichteWeiteZusatzEinzeltor + anschlagprofilZusatz,
      maxX + lichteWeiteZusatzEinzeltor + anschlagprofilZusatz,
    ],
    [MASSE.Gesamtbreite]: [minX + gesamtBreiteZusatz, maxX + gesamtBreiteZusatz],
    [MASSE.Höhe]: [minY, maxY],
    [MASSE.Anschlagprofil]: [null, null],
    [MASSE.PlatzbedarfSchiebetor]: [null, null],
    [MASSE.PlatzbedarfSchiebetorInklusiveFundament]: [null, null],
    [MASSE.PlatzbedarfSchiebetorTiefe]: [null, null],
  };

  const clonedAuswahlDatas: { [key: string]: MAuswahl } = {};
  Object.entries(nameIntervallMapping).forEach((entry) => {
    const name = entry[0];
    const intervall = entry[1];
    const clonedAuswahlData = _.clone(getAuswahlData(name));

    if (Commons.isEmpty(intervall) || Commons.isEmpty(clonedAuswahlData)) return null;

    // Update Hilfstext (Füge Intervallgrenzen in Text ein)
    clonedAuswahlData.Hilfstext = sprintf.vsprintf(clonedAuswahlData.Hilfstext, [
      Maths.round(intervall[0] * 100, 1),
      Maths.round(intervall[1] * 100, 1),
    ]);

    clonedAuswahlDatas[name] = clonedAuswahlData;
  });

  return clonedAuswahlDatas;
}

/**
 * Wrapper für TitleBlock und SizeInput mit immer gleichen Parametern
 * @param what Was wird konfiguriert? "Breite", "Lichte Weite", ...
 * @param disabled Deaktiviert?
 * @param axis "x" | "y"
 * @param min minimal zulässiger Wert
 * @param max maximal zulässiger Wert
 */
function Eingabe({
  what,
  auswahlData,
  min,
  max,
  disabled,
  axis = "x",
  textColor,
}: {
  what: string;
  auswahlData: MAuswahl;
  min: number;
  max: number;
  disabled: boolean;
  axis?: string;
  textColor?: string;
}) {
  const konf = APP.arrangement.konfiguration;
  return (
    <div>
      <TitleBlock auswahlData={auswahlData} small={true} textColor={textColor}></TitleBlock>
      <SizeInput
        key={konf.currentElement.id + "." + what}
        ariaLabel={auswahlData?.AriaText || auswahlData?.TextMass || auswahlData?.Text || what}
        dataInputKey={what}
        axis={axis}
        value={konf.getMass(what as MASSE)}
        setter={(value) => konf.setMass(what as MASSE, value)}
        disabled={disabled}
        className={disabled ? "keepStyle greyedOut" : ""}
        min={min}
        max={max}
        unit={"cm"}
        decimals={1}
        step={0.5}
        conversionFactor={100}
      />
    </div>
  );
}

/**
 * Wrapper für "+- LichteWeiteZusatz ="
 * @param lichteWeiteZusatz
 * @constructor
 */
function LichteWeiteZusatz({
  lichteWeiteZusatz,
  title,
  equalSign = true,
  textColor,
}: {
  lichteWeiteZusatz: number;
  title?: string;
  equalSign?: boolean;
  textColor?: string;
}) {
  return (
    <>
      <ZwischenText text={`&nbsp; ${lichteWeiteZusatz >= 0 ? "+" : "-"} &nbsp;`} />
      <div>
        {!Commons.isEmpty(title) && (
          <TitleBlock text={title} small={true} textColor={textColor}></TitleBlock>
        )}
        <NumberInput
          ariaLabel={I18N("LichteWeite")}
          value={Math.abs(lichteWeiteZusatz * 100)}
          decimals={1}
          disabled={true}
          unit={"cm"}
          className={"abstandInput greyedOut"}
        />
      </div>
      {equalSign && <ZwischenText text={"&nbsp;=&nbsp;"} />}
    </>
  );
}

/**
 * Wrapper für "+ Seitlicher Überstand = Platzbedarf"
 * @param konfiguration
 * @constructor
 */
function EAntriebPlatzbedarf({ konfiguration }: { konfiguration: Konfiguration }) {
  return (
    <>
      <ZwischenText text={`&nbsp;+&nbsp; ${I18N("Seitlicher Überstand")} &nbsp;=&nbsp;`} />
      <div>
        <TitleBlock auswahlData={getAuswahlData(MASSE.Platzbedarf)} small={true}></TitleBlock>
        <SizeInput
          key={MASSE.Platzbedarf}
          ariaLabel={I18N("Seitlicher Überstand")}
          dataInputKey={MASSE.Platzbedarf}
          setter={() => {}}
          min={0}
          max={1000}
          axis={"x"}
          value={konfiguration.getMass(MASSE.Platzbedarf)}
          disabled={true}
          className={"keepStyle greyedOut"}
          unit={"cm"}
          decimals={1}
          step={0.5}
          conversionFactor={100}
        />
      </div>
    </>
  );
}

/**
 * Nur ein div und ein span, gefüllt mit text
 * @param text
 */
function ZwischenText({ text }: { text: string }) {
  return (
    <div>
      <span dangerouslySetInnerHTML={{ __html: text }} aria-hidden="true"></span>
    </div>
  );
}
